<template>
  <Container v-bind="containerProps" :leading="_leading">
    <input
      v-model="currentValue"
      type="text"
      class="flex-grow px-2 h-full w-full bg-transparent text-sm"
      :max="max"
      :min="min"
      :step="step"
      v-bind="attributes"
      v-on="handlers"
      @keypress="handleKeyPress"
      @focusout="handleFocusOut"
      @input="handleInput"
    >

    <template #leading>
      <slot name="leading" />
    </template>

    <template #trailing>
      <slot name="trailing" />
    </template>
  </Container>
</template>

<script setup lang="ts">
import Container from './Container.vue'

const { $auth } = useNuxtApp()

interface Props {
  modelValue?: number | string
  increment?: number
  min?: number
  max?: number
  step?: string
  slim?: boolean
  leading?: string | null
  id?: string | number | null
  name?: string | null
  required?: boolean
  readonly?: boolean
  clear?: boolean
  disabled?: boolean
  size?: string | number | null
  pattern?: string | null
  placeholder?: string | number | null
  trailing?: string | string[] | null
  loading?: boolean
  baseClass?: string | Record<string, any> | string[]
  border?: string | Record<string, any> | string[] | boolean
  rounded?: string | Record<string, any> | string[] | boolean
  shadow?: string | Record<string, any> | string[] | boolean
  highlight?: string | Record<string, any> | string[] | boolean
  defaultStatusClass?: string | Record<string, any> | string[]
  warningStatusClass?: string | Record<string, any> | string[]
  errorStatusClass?: string | Record<string, any> | string[]
  successStatusClass?: string | Record<string, any> | string[]
  disabledClass?: string | Record<string, any> | string[]
  defaultSizeClass?: string | Record<string, any> | string[]
  largeSizeClass?: string | Record<string, any> | string[]
  smallSizeClass?: string | Record<string, any> | string[]
  status?: boolean | string | null
  autofocus?: boolean | null
  tabindex?: string | number | null
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: 0,
  increment: 1,
  min: 0,
  max: Number.MAX_SAFE_INTEGER,
  step: 'any',
  slim: false,
  leading: null,
  id: null,
  name: null,
  required: false,
  readonly: false,
  clear: false,
  disabled: false,
  size: null,
  pattern: null,
  placeholder: null,
  trailing: null,
  loading: false,
  baseClass: 'h-9 w-full relative flex items-center',
  border: 'border dark:border-gray-800',
  rounded: 'rounded-md',
  shadow: 'shadow-sm',
  highlight: 'focus-within:ring-2',
  defaultStatusClass: 'bg-white dark:bg-gray-900',
  warningStatusClass: 'border-yellow-200 bg-yellow-50 dark:bg-yellow-400/20',
  errorStatusClass: 'border-red-300 bg-red-100 dark:bg-red-400/20',
  successStatusClass: 'border-green-200 bg-green-100 dark:bg-green-400/20',
  disabledClass: 'bg-gray-50 dark:bg-gray-800 cursor-not-allowed opacity-75 dark:opacity-50',
  defaultSizeClass: 'p-1',
  largeSizeClass: 'p-4 text-lg',
  smallSizeClass: 'p-px text-sm',
  status: null,
  autofocus: null,
  tabindex: null,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: any): void
  (e: 'blur', value: any): void
  (e: 'focus', value: any): void
  (e: 'change', value: any): void
  (e: 'keyup', value: any): void
  (e: 'keydown', value: any): void
}>()

const valueWhenFocus = ref<any>(null)

const currentValue = computed({
  get () {
    return props.modelValue
  },
  set (value: any) {
    emit('update:modelValue', value)
  },
})

const _leading = computed(() => {
  if (props.leading) {
    return props.leading
  }
  return $auth.info.location.currencySymbol
})

const noStatus = computed(() => props.status === null)
const isError = computed(() => props.status === false || props.status === 'error')
const isSuccess = computed(() => props.status === true || props.status === 'success')
const isWarning = computed(() => props.status === 'warning')

const statusClasses = computed(() => {
  const classes: any[] = []
  if (props.disabled) {
    if (props.disabledClass) classes.push(props.disabledClass)
  }
  if (props.size === null && props.defaultSizeClass) classes.push(props.defaultSizeClass)
  else if (props.size === 'sm' && props.smallSizeClass) classes.push(props.smallSizeClass)
  else if (props.size === 'lg' && props.largeSizeClass) classes.push(props.largeSizeClass)
  if (!props.disabled && noStatus.value && props.defaultStatusClass) classes.push(props.defaultStatusClass)
  if (isError.value && props.errorStatusClass) classes.push(props.errorStatusClass)
  else if (isSuccess.value && props.successStatusClass) classes.push(props.successStatusClass)
  else if (isWarning.value && props.warningStatusClass) classes.push(props.warningStatusClass)
  return classes
})

const currentClass = computed(() => {
  const classes: any[] = [props.baseClass]
  return classes.concat(statusClasses.value)
})

const attributes = computed(() => ({
  id: props.id,
  name: props.name,
  required: props.required,
  readonly: props.readonly,
  clear: props.clear,
  disabled: props.disabled,
  size: props.size,
  pattern: props.pattern,
  placeholder: props.placeholder,
}))

const handlers = computed(() => ({
  blur: onBlur,
  focus: onFocus,
  keyup: onKeyUp,
  keydown: onKeyDown,
}))

const containerProps = computed(() => {
  const classes = [...currentClass.value]
  if (props.border) classes.push(props.border as string)
  if (props.rounded) classes.push(props.rounded as string)
  if (props.shadow) classes.push(props.shadow as string)
  if (props.highlight) classes.push(props.highlight as string)
  return {
    class: classes,
    baseClass: props.baseClass,
    leading: props.leading,
    trailing: props.trailing,
    loading: props.loading,
  }
})

function onBlur (e: Event) {
  emit('blur', e)
  if (currentValue.value !== valueWhenFocus.value) {
    emit('change', currentValue.value)
  }
}

function onFocus (e: Event) {
  emit('focus', e)
  valueWhenFocus.value = currentValue.value
}

function onKeyUp (e: Event) {
  emit('keyup', e)
}

function onKeyDown (e: Event) {
  emit('keydown', e)
}

function handleInput (e: Event) {
  const target = e.target as HTMLInputElement
  const value = Number(target.value)
  if (value < props.min) {
    target.value = String(props.min)
    currentValue.value = props.min
  } else if (value > props.max) {
    target.value = String(props.max)
    currentValue.value = props.max
  }
}

function handleKeyPress (e: KeyboardEvent) {
  const keyCode = (e.keyCode ? e.keyCode : e.which)
  const target = e.target as HTMLInputElement
  const value = target.value
  const i = value.indexOf('.')

  if ((keyCode < 48 || keyCode > 57) && (keyCode !== 46 || i !== -1)) {
    e.preventDefault()
  } else if (target.selectionStart === target.selectionEnd && (i > -1 || (target.selectionEnd ?? 0) > i) && value.split('.')[1]?.length > 1) {
    e.preventDefault()
  }
}

function handleFocusOut ($event: FocusEvent) {
  const target = $event.target as HTMLInputElement
  target.value = (Number(target.value) || 0).toFixed(2)
}
</script>

<style>
  /* Chrome, Safari, Edge, Opera */
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  input[type=number] {
    -moz-appearance: textfield;
  }
</style>
